/**
 * useEVProject Hook
 *
 * Encapsulates EV project state and CRUD operations.
 * Uses the unified project system (useUnifiedProjects) as the data source.
 * EV-specific config is stored in project.apps.flux_ev.config and
 * flattened to top-level for backward compatibility with downstream components.
 *
 * FIX B3: All updaters use functional setState (prev => ...) to avoid stale closures.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API_BASE_URL } from '@vortx/shared-ui';
import { useEVProjects } from './useEVProjects';

/**
 * Default EV project configuration for new projects.
 */
const DEFAULT_EV_CONFIG = {
    project_type: 'fleet',
    site_config: {
        service_voltage: 480,
        service_amperage: 400,
        calculated_capacity_kw: null,
        available_capacity_kw: null,
    },
    fleet: [],
    chargers: [],
    optimization_settings: {
        mode: 'opportunity',
        max_demand_kw: null,
    },
};

/**
 * Flatten a unified project into the EV project shape that downstream
 * components expect (fleet, chargers, site_config at top level).
 */
function flattenEVProject(unifiedProject) {
    if (!unifiedProject) return null;
    const evConfig = unifiedProject.apps?.flux_ev?.config || {};
    // Tariff: EV submeter tariff if present, otherwise main meter tariff
    const meters = unifiedProject.meters || {};
    const evSubmeter = meters.ev_submeter || {};
    const mainMeter = meters.main || {};
    const evSubmeterTariff = evSubmeter.tariff || {};
    const mainTariff = mainMeter.tariff || {};
    const hasEvSubmeter = Boolean(evSubmeterTariff.id);
    const activeTariff = hasEvSubmeter ? evSubmeterTariff : mainTariff;

    return {
        id: unifiedProject.id,
        name: unifiedProject.name,
        description: unifiedProject.description,
        location: unifiedProject.location,
        // Top-level site electrical (managed in ProjectOverviewPage)
        site_capacity_kw: unifiedProject.site_capacity_kw || null,
        site_capacity_amps: unifiedProject.site_capacity_amps || null,
        service_voltage: unifiedProject.service_voltage || null,
        // Tariff from project-level meters (EV submeter > main meter)
        tariff_id: activeTariff.id || null,
        tariff_name: activeTariff.name || null,
        tariff_source: hasEvSubmeter ? 'ev_submeter' : (mainTariff.id ? 'main' : null),
        // Flatten EV-specific fields to top level
        project_type: evConfig.project_type || 'fleet',
        site_config: evConfig.site_config || DEFAULT_EV_CONFIG.site_config,
        fleet: evConfig.fleet || [],
        chargers: evConfig.chargers || [],
        optimization_settings: evConfig.optimization_settings || DEFAULT_EV_CONFIG.optimization_settings,
        depot_config: evConfig.depot_config || null,
        // Timestamps
        created_at: unifiedProject.created_at,
        updated_at: unifiedProject.updated_at,
    };
}

/**
 * Convert a flat EV project's config fields into the unified update payload.
 */
function toUnifiedConfigUpdate(flatProject) {
    return {
        apps: {
            flux_ev: {
                enabled: true,
                config: {
                    project_type: flatProject.project_type,
                    site_config: flatProject.site_config,
                    fleet: flatProject.fleet,
                    chargers: flatProject.chargers,
                    optimization_settings: flatProject.optimization_settings,
                    depot_config: flatProject.depot_config,
                },
            },
        },
    };
}

export function useEVProject(projectId) {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // Unified projects hook — provides list, CRUD, and loading state
    const {
        projects: unifiedProjects,
        loading: projectsLoading,
        error: projectsError,
        loadProjects,
        createProject: unifiedCreateProject,
        updateProject: unifiedUpdateProject,
        deleteProject: unifiedDeleteProject,
    } = useEVProjects();

    // Local state for the current project (flattened EV shape)
    const [currentProject, setCurrentProject] = useState(null);
    const [error, setError] = useState(null);

    // Save status (matches Site DNA pattern)
    const [saveStatus, setSaveStatus] = useState('idle');
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [saveError, setSaveError] = useState(null);

    // Flatten unified projects for the dashboard
    const projects = useMemo(() =>
        unifiedProjects.map(flattenEVProject).filter(Boolean),
        [unifiedProjects]
    );

    // Sync projectsError to local error state
    useEffect(() => {
        if (projectsError) {
            setError(projectsError);
        }
    }, [projectsError]);

    // Track which project we've already attempted a direct fetch for
    const directFetchedRef = useRef(null);

    // Find and set current project when projectId or projects list changes.
    // If the project is not in the filtered list (e.g., navigated via URL for a
    // project that doesn't have flux_ev enabled yet), fall back to a direct fetch
    // and auto-enable the app.
    useEffect(() => {
        if (!projectId) {
            setCurrentProject(null);
            directFetchedRef.current = null;
            return;
        }

        if (projectsLoading) return;

        const found = unifiedProjects.find(p => p.id === projectId);
        if (found) {
            setCurrentProject(flattenEVProject(found));
            setError(null);
            return;
        }

        // Not found in filtered list — try direct fetch (once per projectId).
        // Don't set error here — the async fetchAndEnable handles error states.
        // Setting error here would overwrite a successfully fetched project when
        // the effect re-runs after loadProjects().
        if (!currentUser || directFetchedRef.current === projectId) {
            return;
        }

        directFetchedRef.current = projectId;
        let cancelled = false;

        const fetchAndEnable = async () => {
            try {
                const token = await currentUser.getIdToken();

                // Fetch the project directly (no app filter)
                const res = await fetch(
                    `${API_BASE_URL}/api/projects/v1/projects/${projectId}`,
                    { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
                );

                if (!res.ok) {
                    if (!cancelled) {
                        setError('Project not found');
                        setCurrentProject(null);
                    }
                    return;
                }

                const data = await res.json();
                const project = data.project || data;

                // Auto-enable flux_ev if not already enabled
                if (!project.apps?.flux_ev?.enabled) {
                    const enableRes = await fetch(
                        `${API_BASE_URL}/api/projects/v1/projects/${projectId}/apps/flux_ev`,
                        {
                            method: 'PUT',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ config: {} }),
                        }
                    );
                    if (enableRes.ok) {
                        const enableData = await enableRes.json();
                        const enabled = enableData.project || enableData;
                        if (!cancelled) {
                            setCurrentProject(flattenEVProject(enabled));
                            setError(null);
                            // Refresh the filtered list so subsequent interactions work
                            loadProjects();
                        }
                        return;
                    }
                }

                // Project exists and has flux_ev enabled (race with list load)
                if (!cancelled) {
                    setCurrentProject(flattenEVProject(project));
                    setError(null);
                    loadProjects();
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to fetch project directly:', err);
                    setError('Project not found');
                    setCurrentProject(null);
                }
            }
        };

        fetchAndEnable();

        return () => { cancelled = true; };
    }, [projectId, unifiedProjects, projectsLoading, currentUser, loadProjects]);

    // CRUD operations
    const createProject = useCallback(async (projectType = 'fleet') => {
        if (!currentUser) return;
        try {
            const projectName = projectType === 'depot' ? 'New Depot Project' : 'New EV Project';
            const evConfig = {
                ...DEFAULT_EV_CONFIG,
                project_type: projectType,
            };

            // For depot projects, add depot_config
            if (projectType === 'depot') {
                evConfig.depot_config = {
                    vehicle_id: null,
                    charger_count: null,
                    charger_rating_kw: null,
                    decomposition_result: null,
                };
            }

            const project = await unifiedCreateProject({
                name: projectName,
                apps: {
                    flux_ev: {
                        enabled: true,
                        config: evConfig,
                    },
                },
            });

            const defaultSection = projectType === 'depot' ? 'upload' : 'site';
            navigate(`/ev/${project.id}/${defaultSection}`);
        } catch (err) {
            setError(err.message);
        }
    }, [currentUser, navigate, unifiedCreateProject]);

    const saveProject = useCallback(async () => {
        if (!currentUser || !projectId || !currentProject) return;

        try {
            setSaveStatus('saving');
            setSaveError(null);
            await unifiedUpdateProject(projectId, toUnifiedConfigUpdate(currentProject));
            setSaveStatus('saved');
            setLastSavedAt(new Date().toISOString());
        } catch (err) {
            setSaveStatus('error');
            setSaveError(err.message);
        }
    }, [currentUser, projectId, currentProject, unifiedUpdateProject]);

    const deleteProject = useCallback(async (idToDelete) => {
        // Support both usage patterns: deleteProject() from project view, deleteProject(id) from dashboard
        const targetId = idToDelete || projectId;
        if (!currentUser || !targetId) return;

        try {
            await unifiedDeleteProject(targetId);
            // Only navigate if we're deleting the current project
            if (targetId === projectId) {
                navigate('/ev');
            }
        } catch (err) {
            setError(err.message);
            throw err; // Re-throw so caller can handle
        }
    }, [currentUser, projectId, navigate, unifiedDeleteProject]);

    const renameProject = useCallback(async (idToRename, newName) => {
        if (!currentUser || !idToRename || !newName) return;

        try {
            await unifiedUpdateProject(idToRename, { name: newName });
            // Update current project if it's the one being renamed
            if (idToRename === projectId) {
                setCurrentProject(prev => prev ? { ...prev, name: newName } : null);
            }
        } catch (err) {
            setError(err.message);
            throw err; // Re-throw so caller can handle
        }
    }, [currentUser, projectId, unifiedUpdateProject]);

    // Autosave: 1s debounce when dirty
    useEffect(() => {
        if (!currentProject || !projectId || saveStatus !== 'dirty') return;

        const timeout = setTimeout(() => {
            saveProject();
        }, 1000);

        return () => clearTimeout(timeout);
    }, [currentProject, projectId, saveStatus, saveProject]);

    // Revert to idle 2s after saved
    useEffect(() => {
        if (saveStatus !== 'saved') return;

        const timeout = setTimeout(() => {
            setSaveStatus('idle');
        }, 2000);

        return () => clearTimeout(timeout);
    }, [saveStatus]);

    // Field updaters — all use functional updaters to avoid stale closures (FIX B3)
    const updateFleet = useCallback((fleet) => {
        setCurrentProject(prev => ({ ...prev, fleet }));
        setSaveStatus('dirty');
    }, []);

    const updateName = useCallback((name) => {
        setCurrentProject(prev => ({ ...prev, name }));
        setSaveStatus('dirty');
    }, []);

    const updateChargers = useCallback((chargers) => {
        setCurrentProject(prev => ({ ...prev, chargers }));
        setSaveStatus('dirty');
    }, []);

    const updateSiteConfig = useCallback((siteConfig) => {
        setCurrentProject(prev => ({ ...prev, site_config: siteConfig }));
        setSaveStatus('dirty');
    }, []);

    const clearError = useCallback(() => setError(null), []);

    // Reload current project (refresh from unified API)
    const reloadProject = useCallback(async () => {
        if (!projectId) return;
        try {
            await loadProjects();
        } catch (err) {
            setError(err.message);
        }
    }, [projectId, loadProjects]);

    // Site capacity from top-level project (managed in ProjectOverviewPage)
    const siteCapacity = currentProject?.site_capacity_kw || null;

    return {
        // State
        projects,
        currentProject,
        loading: projectsLoading,
        error,
        saveStatus,
        lastSavedAt,
        saveError,
        siteCapacity,

        // Actions
        createProject,
        saveProject,
        deleteProject,
        renameProject,
        reloadProject,

        // Field updaters
        updateFleet,
        updateName,
        updateChargers,
        updateSiteConfig,

        // Utilities
        clearError,
    };
}
